import React, { FunctionComponent, useReducer } from "react";
import { RadioGroup } from "@headlessui/react";
import { CakeIcon } from "@heroicons/react/24/outline";
import getStripe from "../utils/get-stripe";
import { fetchPostJSON } from "../utils/api-helpers";
import { formatAmountForDisplay } from "../utils/stripe-helpers";
import * as config from "../config";

/*
  This example requires Tailwind CSS v2.0+ 
  
  This example requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/

type Option = {
  name: string;
  description: string;
  amount?: number;
  hasInput?: boolean;
};

const settings: Option[] = [
  { name: "$10", description: "Cake Ingredients", amount: 10 },
  { name: "$25", description: "1/2 x Birthday Cake", amount: 25 },
  { name: "$50", description: "1 x Birthday Cake", amount: 50 },
  { name: "$100", description: "2 x Birthday Cakes", amount: 100 },
  { name: "Custom", description: "Choose a custom amount", hasInput: true },
];

function classNames(...classes: string[]): string {
  return classes.filter(Boolean).join(" ");
}

interface DontateOptionsProps {
  selected: Option;
  setSelected: (option: Option) => void;
}

const DonateOptions: FunctionComponent<DontateOptionsProps> = ({
  selected,
  setSelected,
}) => {
  return (
    <RadioGroup value={selected} onChange={setSelected}>
      <RadioGroup.Label className="sr-only">Privacy setting</RadioGroup.Label>
      <div className="bg-transparent rounded-md -space-y-px">
        {settings.map((setting, settingIdx) => (
          <RadioGroup.Option
            key={setting.name}
            value={setting}
            className={({ checked }) =>
              classNames(
                settingIdx === 0 ? "rounded-tl-md rounded-tr-md" : "",
                settingIdx === settings.length - 1
                  ? "rounded-bl-md rounded-br-md"
                  : "",
                checked ? "bg-brand-pink z-10" : "border-brand-pink",
                "relative border border-brand-pink p-4 flex cursor-pointer focus:outline-none"
              )
            }
          >
            {({ active, checked }) => (
              <>
                <span
                  className={classNames(
                    checked
                      ? "bg-brand-pink border-white"
                      : "bg-white border-brand-pink",
                    active ? "ring-2 ring-offset-2 ring-brand-pink" : "",
                    "h-4 w-4 mt-0.5 cursor-pointer rounded-full border flex items-center justify-center"
                  )}
                  aria-hidden="true"
                >
                  <span className="rounded-full bg-white w-1.5 h-1.5" />
                </span>
                <div className="ml-3 flex flex-col">
                  <RadioGroup.Label
                    as="span"
                    className={classNames(
                      checked ? "text-white" : "text-brand-navy",
                      "block text-sm font-bold"
                    )}
                  >
                    {setting.name}
                  </RadioGroup.Label>
                  <RadioGroup.Description
                    as="span"
                    className={classNames(
                      checked ? "text-white" : "text-brand-navy",
                      "block text-xs uppercase"
                    )}
                  >
                    {setting.description}
                  </RadioGroup.Description>
                </div>
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );
};

type State = { selected: Option; amount: number; isLoading: boolean };
type Action =
  | { type: "loading" }
  | { type: "loading-complete" }
  | { type: "select"; option: Option }
  | { type: "amount"; amount: number };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "select":
      return {
        isLoading: false,
        selected: action.option,
        amount: action.option.amount || state.amount,
      };
    case "amount":
      return { ...state, amount: action.amount };

    case "loading":
      return { ...state, isLoading: true };

    case "loading-complete":
      return { ...state, isLoading: false };

    default:
      return state;
  }
}

const CheckoutForm: FunctionComponent = () => {
  const [{ selected, amount, isLoading }, dispatch] = useReducer(reducer, {
    isLoading: false,
    selected: settings[0],
    amount: settings[0].amount || 5,
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    dispatch({ type: "loading" });
    // Create a Checkout Session.
    const response = await fetchPostJSON("/api/checkout_sessions", {
      amount,
    });

    if (response.statusCode === 500) {
      console.error(response.message);
      return;
    }

    // Redirect to Checkout.
    const stripe = await getStripe();
    const { error } = await stripe!.redirectToCheckout({
      // Make the id field from the Checkout Session creation API response
      // available to this file, so you can provide it as parameter here
      // instead of the {{CHECKOUT_SESSION_ID}} placeholder.
      sessionId: response.id,
    });
    // If `redirectToCheckout` fails due to a browser or network
    // error, display the localized error message to your customer
    // using `error.message`.
    console.warn(error.message);
    dispatch({ type: "loading-complete" });
  };

  return (
    <div className="mt-10 pb-12 sm:pb-16">
      <div className="relative max-w-xl lg:max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 bg-white/50 rounded-lg">
        <form id="donate" onSubmit={handleSubmit}>
          <DonateOptions
            selected={selected}
            setSelected={(option: Option) =>
              dispatch({ type: "select", option })
            }
          />
          {selected.hasInput ? (
            <div className="mt-2">
              <input
                type="number"
                className="shadow-sm focus:ring-brand-teal focus:border-brand-teal block w-full sm:text-sm border-gray-300 rounded-md"
                value={amount}
                min={5}
                onChange={(e) =>
                  dispatch({ type: "amount", amount: Number(e.target.value) })
                }
              />
              <p className="mt-2 text-sm text-gray-500" id="email-description">
                Enter a custom donation amount
              </p>
            </div>
          ) : null}
          <div className="mt-4">
            <button
              className="inline-flex w-full justify-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-brand-pink hover:bg-brand-pink"
              type="submit"
              disabled={isLoading}
            >
              Donate {formatAmountForDisplay(amount, config.CURRENCY)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutForm;
